import { ChangeDetectorRef, Directive, ElementRef, inject, input } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { FeedbackService } from '@resident-nx/shared';

import { heicTo } from 'heic-to';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'input[type=file]',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: FileUploadValueAccessorDirective, multi: true },
  ],
  standalone: true,
  host: {
    '(change)': 'emitFiles($event.target)',
    '(blur)': 'onBlur()',
  },
})
export class FileUploadValueAccessorDirective implements ControlValueAccessor {
  /**
   * additive = if you add new files, it gets added to the existing files
   *
   * if you change the value of a <input type="file"> element,
   * the previous value gets replaced by default. This is not optimal in a
   * UX scenario where a user wants to add multiple files one after
   * another, e.g. from different folders
   */
  additive = input(false);
  compressImageMaxWidth = input(1920);
  compressImageQuality = input(0.7);
  compressImages = input(true);
  inputField: ElementRef<HTMLInputElement> = inject(ElementRef<HTMLInputElement>);
  value: unknown;
  files: FileList | [] = [];
  #cdr = inject(ChangeDetectorRef);
  #translate = inject(TranslateService);
  #feedback = inject(FeedbackService);

  onChange: (value: FileList) => void = () => null;
  onTouched: () => void = () => null;

  async emitFiles(event: EventTarget) {
    const files = (event as HTMLInputElement).files;
    // the `accept` attribute is only a hint; native pickers (macOS/iOS "Show
    // Options" → All Files) let users bypass it, so re-check every file here
    // and drop anything that doesn't match the accepted types.
    const accept = this.inputField.nativeElement.accept;
    const acceptTokens = this.parseAcceptTokens(accept);
    const selected = Array.from(files);
    const accepted = selected.filter(file => this.isFileAccepted(file, acceptTokens));

    const rejected = selected.filter(file => !accepted.includes(file));
    if (rejected.length) {
      this.notifyRejected(accept);
    }

    // compress each file if it’s an image
    const processedFiles: File[] = [];
    for (const file of accepted) {
      if (this.compressImages() && file.type.startsWith('image/')) {
        const maxWidth = this.compressImageMaxWidth();
        const quality = this.compressImageQuality();
        const compressed = await this.compressImage(file, { maxWidth, quality });
        processedFiles.push(compressed);
      } else {
        processedFiles.push(file);
      }
    }

    // additive describes adding multiple files one after another
    const newFiles = this.additive()
      ? this.addFilesToCurrentFileList(processedFiles)
      : this.createFileList(processedFiles);

    this.inputField.nativeElement.files = newFiles;
    this.files = newFiles;
    this.onChange(newFiles.length ? newFiles : null);
    this.#cdr.markForCheck();
  }

  onBlur() {
    this.onTouched();
  }

  writeValue(value: FileList) {
    if (value) {
      this.inputField.nativeElement.files = value;
      this.files = value;
    }
    if (value === null) {
      const emptyFiles = new DataTransfer().files;
      this.inputField.nativeElement.files = emptyFiles;
      // reset internal cache too, else additive uploads re-add the cleared files
      this.files = emptyFiles;
    }
  }

  /**
   * Surfaces an error toast when the user selects disallowed file types (e.g.
   * via the native picker's "All Files" escape hatch). Lives in the directive
   * so every `input[type=file]` gets the notice without per-consumer wiring.
   */
  private notifyRejected(accept: string) {
    const message = this.#translate.instant('upload_invalid_file_type_l');
    this.#feedback.error({
      message: `${message} ${accept}`.trim(),
      duration: 5000,
    });
  }

  registerOnChange(fn: (value: FileList) => void) {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  private parseAcceptTokens(accept: string): string[] {
    return (accept ?? '')
      .split(',')
      .map(token => token.trim().toLowerCase())
      .filter(Boolean);
  }

  /**
   * Mirrors the browser's `accept` matching so files chosen via the native
   * picker's "All Files" escape hatch are filtered out client-side.
   *
   * Accepts when there are no tokens (empty/absent `accept`). Otherwise the
   * file must match at least one token:
   *  - extension token (".pdf") → case-insensitive filename suffix
   *  - wildcard MIME ("image/*") → file's MIME type prefix
   *  - exact MIME ("application/pdf") → file's MIME type
   */
  private isFileAccepted(file: File, tokens: string[]): boolean {
    if (tokens.length === 0) {
      return true;
    }

    const fileType = (file.type || '').toLowerCase();
    const fileName = file.name.toLowerCase();

    return tokens.some(token => {
      if (token.startsWith('.')) {
        return fileName.endsWith(token);
      }
      if (token.endsWith('/*')) {
        return fileType.startsWith(token.slice(0, token.indexOf('/') + 1));
      }
      return fileType === token;
    });
  }

  private createFileList(files: File[]) {
    const dataTransfer = new DataTransfer();
    for (const file of files) {
      dataTransfer.items.add(file);
    }
    return dataTransfer.files;
  }

  private addFilesToCurrentFileList(files: File[]): FileList {
    const allFiles = [...this.files, ...files];
    return this.createFileList(allFiles);
  }

  /**
   * Compresses an image file client-side using an offscreen canvas.
   *
   * The process:
   *  - Decode the image into an ImageBitmap (fast, async, no DOM pollution)
   *  - Compute a scaling factor if the image exceeds the allowed max width
   *  - Draw the image onto a canvas at the new scaled size
   *  - Convert the canvas back into a Blob (compressed image data)
   *  - Wrap the Blob into a File object (so it behaves like the original)
   */
  private async compressImage(
    file: File,
    options: { maxWidth: number; quality: number }
  ): Promise<File> {
    // convert HEIC files (iOS) to JPEG, as HEIC is not compressable
    const isHeicFormat = /image\/heic|image\/heif/i.test(file.type);
    if (isHeicFormat) {
      try {
        const jpeg = await heicTo({
          blob: file,
          type: 'image/jpeg',
          quality: 0.5,
        });
        file = new File([jpeg as BlobPart], file.name.replace(/\.[^/.]+$/, '.jpg'), {
          type: 'image/jpeg',
        });
      } catch (err) {
        console.warn('HEIC not supported, skipping compression:', err);
        return file;
      }
    }
    // Decode the image asynchronously into an ImageBitmap.
    const imageBitmap = await createImageBitmap(file);
    // Determine the scaling factor based on maxWidth.
    // If the image is already smaller, keep the original size (scale = 1).
    const scale = Math.min(1, options.maxWidth / imageBitmap.width);
    // Calculate new dimensions, preserving the aspect ratio.
    const width = Math.floor(imageBitmap.width * scale);
    const height = Math.floor(imageBitmap.height * scale);
    // Create an offscreen <canvas> and draw the scaled image onto it.
    // The canvas acts as a bitmap buffer for re-encoding the image.
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    context.drawImage(imageBitmap, 0, 0, width, height);
    // Convert the canvas contents into a Blob.
    // The third argument (quality) applies only for lossy formats (e.g., JPEG/WebP).
    const blob: Blob = await new Promise(resolve =>
      canvas.toBlob(resolve, file.type || 'image/jpeg', options.quality)
    );
    // Wrap the Blob into a File so that
    // it behaves like the original uploaded File (with name/type).
    return new File([blob], file.name, { type: blob.type });
  }
}
